import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Card, Form, Input, message, Modal, Spin, Upload, Typography, Tabs, Checkbox } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { Image as AntdImage } from 'antd';
import imageCompression from 'browser-image-compression';

import { getAdv, createAdv, updateAdv, deleteAdv, uploadAdvImage } from 'src/features/adv/adv_home.api';
import { RoleEnum } from 'src/features/users/users.types';
import ComponentRoleProtected from 'src/components/blocks/ComponentRoleProtected';

const SUPPORTED_LANGS = ['ru', 'en'];

interface AdvTranslation {
  lang: string;
  homeTitle?: string;
  modalTitle?: string;
  modalDescription?: string;
}

interface AdvData {
  home_preview_url: string;
  modal_preview_url?: string | null;
  show_at_session_start: boolean;
  translations: {
    [lang: string]: Omit<AdvTranslation, 'lang'>;
  };
}

interface AdvTypes {
  id: string;
  companyId: string;
  homePreviewUrl: string;
  modalPreviewUrl: string | null;
  showAtSessionStart: boolean;
  translations: AdvTranslation[];
}

interface AdvFormValues {
  home_preview_url: string;
  modal_preview_url?: string;
  show_at_session_start: boolean;
  translations: {
    [lang: string]: {
      homeTitle: string;
      modalTitle: string;
      modalDescription: string;
    };
  };
}

const CompanyAdvHomeTab: React.FC = () => {
  const { companyId } = useParams() as { companyId: string };
  const [homeForm] = Form.useForm<AdvFormValues>();
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [homeAdv, setHomeAdv] = useState<AdvTypes | null>(null);
  const [uploading, setUploading] = useState(false);
  const [activeLang, setActiveLang] = useState<string>('ru');

  const currentTranslation = useMemo(() => {
    if (editing) {
      return (
        homeForm.getFieldValue(['translations', activeLang]) || {
          homeTitle: '',
          modalTitle: '',
          modalDescription: '',
        }
      );
    } else {
      if (!homeAdv || !Array.isArray(homeAdv.translations)) {
        return { homeTitle: '', modalTitle: '', modalDescription: '' };
      }
      const found = homeAdv.translations.find(t => t.lang === activeLang);
      return {
        homeTitle: found?.homeTitle || '',
        modalTitle: found?.modalTitle || '',
        modalDescription: found?.modalDescription || '',
      };
    }
  }, [editing, homeAdv, activeLang, homeForm]);

  const fetchHomeAdv = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAdv({ companyId });
      if (response.status === 200) {
        setHomeAdv(response.data);
      } else if (response.status === 404) {
        setHomeAdv(null);
      } else {
        console.error('Server error on fetch:', response);
        message.error('Ошибка сервера');
        setHomeAdv(null);
      }
    } catch (error) {
      console.error('Failed to fetch home advertisement:', error);
      message.error('Ошибка загрузки');
      setHomeAdv(null);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchHomeAdv();
  }, [fetchHomeAdv]);

  const setHomeFormValuesFromAdvAndLang = useCallback(
    (advertisement: AdvTypes, lang: string) => {
      const translationsAsObject: AdvFormValues['translations'] = {} as any;
      SUPPORTED_LANGS.forEach(l => {
        translationsAsObject[l] = {
          homeTitle: '',
          modalTitle: '',
          modalDescription: '',
        };
      });
      if (Array.isArray(advertisement.translations)) {
        advertisement.translations.forEach(translation => {
          if (SUPPORTED_LANGS.includes(translation.lang)) {
            translationsAsObject[translation.lang] = {
              homeTitle: translation.homeTitle || '',
              modalTitle: translation.modalTitle || '',
              modalDescription: translation.modalDescription || '',
            };
          }
        });
      }

      homeForm.setFieldsValue({
        home_preview_url: advertisement.homePreviewUrl,
        modal_preview_url: advertisement.modalPreviewUrl || undefined,
        show_at_session_start: advertisement.showAtSessionStart ?? false,
        translations: translationsAsObject,
      });
    },
    [homeForm]
  );

  useEffect(() => {
    if (homeAdv && !editing) {
      const allLangs = homeAdv.translations?.map(t => t.lang) || [];
      const langToUse = allLangs.includes('ru') ? 'ru' : allLangs[0] || 'ru';
      setActiveLang(langToUse);
      setHomeFormValuesFromAdvAndLang(homeAdv, langToUse);
    }
  }, [homeAdv, editing, setHomeFormValuesFromAdvAndLang]);

  const handleLangChange = useCallback((key: string) => {
    setActiveLang(key);
  }, []);

  const handleUpload = useCallback(
    async (file: UploadFile, assetType: 'home' | 'modal') => {
      setUploading(true);
      try {
        let maxWidth = 1024;
        let maxHeight = 1024;

        if (assetType === 'home') {
          maxWidth = 512;
          maxHeight = 350;
        } else if (assetType === 'modal') {
          maxWidth = 680;
          maxHeight = 2000;
        }

        const options = {
          maxSizeMB: 5,
          maxWidthOrHeight: Math.max(maxWidth, maxHeight),
          useWebWorker: true,
        };

        const fileAsFile = file as unknown as File;
        const compressedFile = await imageCompression(fileAsFile, options);

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          throw new Error('Could not get canvas context');
        }

        const img = new Image();
        img.src = URL.createObjectURL(compressedFile);

        const resizedFile = await new Promise<File>((resolve, reject) => {
          img.onload = () => {
            let newWidth = img.width;
            let newHeight = img.height;

            if (assetType === 'modal') {
              if (newWidth <= maxWidth && newHeight <= maxHeight) {
                canvas.width = newWidth;
                canvas.height = newHeight;
                ctx.drawImage(img, 0, 0, newWidth, newHeight);
              } else {
                if (newWidth > maxWidth) {
                  newHeight *= maxWidth / newWidth;
                  newWidth = maxWidth;
                }
                if (newHeight > maxHeight) {
                  newWidth *= maxHeight / newHeight;
                  newHeight = maxHeight;
                }
                canvas.width = newWidth;
                canvas.height = newHeight;
                ctx.drawImage(img, 0, 0, newWidth, newHeight);
              }
            } else {
              if (newWidth > maxWidth) {
                newHeight *= maxWidth / newWidth;
                newWidth = maxWidth;
              }
              if (newHeight > maxHeight) {
                newWidth *= maxHeight / newHeight;
                newHeight = maxHeight;
              }
              canvas.width = newWidth;
              canvas.height = newHeight;
              ctx.drawImage(img, 0, 0, newWidth, newHeight);
            }

            canvas.toBlob(
              blob => {
                if (!blob) {
                  reject(new Error('Canvas toBlob failed'));
                  return;
                }
                const resizedFile = new File([blob], file.name, { type: blob.type });
                resolve(resizedFile);
              },
              file.type || 'image/jpeg',
              0.85
            );
          };
          img.onerror = reject;
        });

        const presignedData = await uploadAdvImage({ companyId, filename: resizedFile.name, assetType });

        if (!presignedData || !presignedData.fields || !presignedData.url) {
          throw new Error('Invalid presigned data');
        }

        const formData = new FormData();
        Object.keys(presignedData.fields).forEach(key => {
          formData.append(key, presignedData.fields[key]);
        });
        formData.append('file', resizedFile);

        const uploadResponse = await fetch(presignedData.url, {
          method: 'POST',
          body: formData,
        });

        if (!uploadResponse.ok) {
          throw new Error(`Upload failed: ${uploadResponse.statusText}`);
        }

        const s3Url = `https://storage.yandexcloud.net/viavr.global/${presignedData.fields.key}`.trim();

        homeForm.setFieldsValue({ [`${assetType}_preview_url`]: s3Url });
        if (homeAdv) {
          if (assetType === 'home') {
            setHomeAdv({ ...homeAdv, homePreviewUrl: s3Url });
          } else {
            setHomeAdv({ ...homeAdv, modalPreviewUrl: s3Url });
          }
        }

        message.success(`Изображение загружено и оптимизировано`);
      } catch (error) {
        message.error(`Ошибка загрузки: ${(error as Error).message}`);
      } finally {
        setUploading(false);
      }
    },
    [companyId, homeAdv, homeForm]
  );

  const handleRemoveImage = useCallback(
    (assetType: 'home' | 'modal') => {
      Modal.confirm({
        title: 'Удалить изображение?',
        content: 'Это действие нельзя отменить.',
        onOk: () => {
          if (assetType === 'home') {
            homeForm.setFieldsValue({ home_preview_url: '' });
            if (homeAdv) {
              setHomeAdv({ ...homeAdv, homePreviewUrl: '' });
            }
          } else {
            homeForm.setFieldsValue({ modal_preview_url: undefined });
            if (homeAdv) {
              setHomeAdv({ ...homeAdv, modalPreviewUrl: null });
            }
          }
          message.success('Изображение удалено');
        },
      });
    },
    [homeAdv, homeForm]
  );

  const handleSave = useCallback(async () => {
    if (uploading) {
      message.warning('Дождитесь загрузки изображений');
      return;
    }

    try {
      await homeForm.validateFields();
    } catch (error: any) {
      if (error?.errorFields) {
        message.error('Ошибка ввода: проверьте ограничения по длине и обязательные поля');
        return;
      }
      throw error;
    }

    const finalValues = homeForm.getFieldsValue(true);

    let hasAtLeastOneCompleteLocale = false;
    for (const lang of SUPPORTED_LANGS) {
      const t = finalValues.translations[lang] || {};
      const hasAnyField = t.homeTitle?.trim() || t.modalTitle?.trim() || t.modalDescription?.trim();
      if (hasAnyField) {
        if (!t.homeTitle?.trim() || !t.modalTitle?.trim() || !t.modalDescription?.trim()) {
          message.error(`В локализации "${lang.toUpperCase()}" заполнены не все обязательные поля.`);
          return;
        }
        hasAtLeastOneCompleteLocale = true;
      }
    }

    if (!hasAtLeastOneCompleteLocale) {
      message.error('Заполните хотя бы одну локализацию полностью (ru или en).');
      return;
    }

    const baseData: AdvData = {
      home_preview_url: finalValues.home_preview_url,
      modal_preview_url: finalValues.modal_preview_url || null,
      show_at_session_start: finalValues.show_at_session_start,
      translations: finalValues.translations || {},
    };

    try {
      let response;
      if (homeAdv) {
        response = await updateAdv({ companyId, data: baseData });
      } else {
        response = await createAdv({ companyId, data: baseData });
      }
      setHomeAdv(response.data);
      setEditing(false);
      message.success('Реклама успешно сохранена');
    } catch (error: any) {
      console.error('Save error:', error);
      if (error?.response?.status === 409) {
        message.error('Реклама для этой компании уже существует. Используйте кнопку "Редактировать".');
      } else {
        message.error('Ошибка сохранения');
      }
    }
  }, [activeLang, companyId, homeAdv, homeForm, uploading]);

  const handleDelete = useCallback(async () => {
    Modal.confirm({
      title: 'Удалить рекламу?',
      content: 'Это действие нельзя отменить.',
      onOk: async () => {
        try {
          await deleteAdv({ companyId });
          message.success('Реклама удалена');
          setHomeAdv(null);
          setEditing(false);
          homeForm.resetFields();
        } catch (error) {
          message.error('Ошибка удаления');
        }
      },
    });
  }, [companyId, homeForm]);

  const handleAddClick = () => {
    const initialTranslations: AdvFormValues['translations'] = {} as any;
    SUPPORTED_LANGS.forEach(lang => {
      initialTranslations[lang] = {
        homeTitle: '',
        modalTitle: '',
        modalDescription: '',
      };
    });

    homeForm.setFieldsValue({
      home_preview_url: '',
      modal_preview_url: undefined,
      show_at_session_start: false,
      translations: initialTranslations,
    });
    setEditing(true);
    setActiveLang('ru');
  };

  if (loading) return <Spin />;

  if (!homeAdv && !editing) {
    return (
      <Card>
        <Typography.Title level={4}>Реклама на главном экране</Typography.Title>
        <ComponentRoleProtected roles={[RoleEnum.admin, RoleEnum.companyContentManager]}>
          <div style={{ display: 'flex', gap: '12px', marginBottom: 16 }}>
            <Button type='primary' icon={<PlusOutlined />} onClick={handleAddClick}>
              Добавить рекламу
            </Button>
          </div>
        </ComponentRoleProtected>
      </Card>
    );
  }

  if (homeAdv && !Array.isArray(homeAdv.translations)) {
    return (
      <Card>
        <Typography.Title level={4}>Реклама на главном экране</Typography.Title>
        <Typography.Text type='danger'>Ошибка: Данные рекламы повреждены.</Typography.Text>
      </Card>
    );
  }

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Typography.Title level={4}>Реклама на главном экране</Typography.Title>
        <div>
          {editing ? (
            <>
              <Button onClick={() => setEditing(false)} style={{ marginRight: 8 }}>
                Отмена
              </Button>
              <Button type='primary' onClick={handleSave} loading={uploading}>
                Сохранить
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => setEditing(true)} style={{ marginRight: 8 }}>
                Редактировать
              </Button>
              <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
                Удалить
              </Button>
            </>
          )}
        </div>
      </div>

      <Tabs
        activeKey={activeLang}
        onChange={handleLangChange}
        items={SUPPORTED_LANGS.map(lang => ({ label: lang.toUpperCase(), key: lang }))}
        style={{ marginBottom: 24 }}
      />

      <Form form={homeForm} layout='vertical'>
        <Typography.Title level={5} style={{ marginBottom: 16 }}>
          Рекламная карточка
        </Typography.Title>

        <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
          {editing ? (
            <Form.Item
              label={<strong>Превью (512х350)</strong>}
              style={{ flex: '0 0 120px' }}
              name='home_preview_url'
              rules={[{ required: true, message: 'Превью обязательно' }]}
            >
              {homeForm.getFieldValue('home_preview_url') ? (
                <AntdImage src={homeForm.getFieldValue('home_preview_url')} width={120} />
              ) : null}
              <Upload
                beforeUpload={file => {
                  handleUpload(file, 'home');
                  return false;
                }}
                accept='image/*'
                showUploadList={false}
                disabled={uploading}
              >
                <Button type='primary' size='small' style={{ marginTop: 8 }}>
                  {homeForm.getFieldValue('home_preview_url') ? 'Обновить' : 'Загрузить'}
                </Button>
              </Upload>
            </Form.Item>
          ) : (
            <div style={{ flex: '0 0 120px' }}>
              <div style={{ marginBottom: 4 }}>
                <strong>Превью (512х350)</strong>
              </div>
              {homeAdv?.homePreviewUrl ? (
                <AntdImage src={homeAdv.homePreviewUrl} width={120} />
              ) : (
                <Typography.Text type='secondary'>—</Typography.Text>
              )}
            </div>
          )}

          {editing ? (
            <Form.Item
              label={<strong>Название</strong>}
              style={{ flex: 1 }}
              name={['translations', activeLang, 'homeTitle']}
              rules={[
                { required: true, message: 'Название обязательно' },
                { max: 50, message: 'Название должно быть не более 50 символов' },
              ]}
            >
              <Input placeholder='Введите название' />
            </Form.Item>
          ) : (
            <div style={{ flex: 1 }}>
              <div style={{ marginBottom: 4 }}>
                <strong>Название</strong>
              </div>
              <Typography.Text>{currentTranslation.homeTitle || '—'}</Typography.Text>
            </div>
          )}
        </div>

        <Typography.Title level={5} style={{ marginBottom: 16 }}>
          Модальное окно с подробной информацией
        </Typography.Title>

        <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
          {editing ? (
            <Form.Item
              label={<strong>Превью (680×...)</strong>}
              style={{ flex: '0 0 120px' }}
              name='modal_preview_url'
              rules={[{ required: false }]}
            >
              {homeForm.getFieldValue('modal_preview_url') ? (
                <>
                  <AntdImage src={homeForm.getFieldValue('modal_preview_url')} width={120} />
                  <Button danger size='small' onClick={() => handleRemoveImage('modal')} style={{ marginTop: 8 }}>
                    Удалить
                  </Button>
                </>
              ) : (
                <Upload
                  beforeUpload={file => {
                    handleUpload(file, 'modal');
                    return false;
                  }}
                  accept='image/*'
                  showUploadList={false}
                  disabled={uploading}
                >
                  <Button type='primary' size='small' style={{ marginTop: 8 }}>
                    Загрузить
                  </Button>
                </Upload>
              )}
            </Form.Item>
          ) : (
            <div style={{ flex: '0 0 120px' }}>
              <div style={{ marginBottom: 4 }}>
                <strong>Превью (680×...)</strong>
              </div>
              {homeAdv?.modalPreviewUrl ? (
                <AntdImage src={homeAdv.modalPreviewUrl} width={120} />
              ) : (
                <Typography.Text type='secondary'>—</Typography.Text>
              )}
            </div>
          )}

          <div style={{ flex: 1 }}>
            {editing ? (
              <>
                <Form.Item
                  label={<strong>Заголовок</strong>}
                  name={['translations', activeLang, 'modalTitle']}
                  rules={[
                    { required: true, message: 'Заголовок обязательно' },
                    { max: 100, message: 'Заголовок должен быть не более 100 символов' },
                  ]}
                >
                  <Input placeholder='Введите заголовок' />
                </Form.Item>
                <Form.Item
                  label={<strong>Описание</strong>}
                  name={['translations', activeLang, 'modalDescription']}
                  rules={[
                    { required: true, message: 'Описание обязательно' },
                    { max: 8000, message: 'Описание должно быть не более 8000 символов' },
                  ]}
                >
                  <Input.TextArea rows={3} placeholder='Введите описание не более 8000 символов' />
                </Form.Item>
                <Form.Item name='show_at_session_start' valuePropName='checked' style={{ marginBottom: 0 }}>
                  <Checkbox>Открыть в начале сессии</Checkbox>
                </Form.Item>
              </>
            ) : (
              <>
                <div style={{ marginBottom: 8 }}>
                  <div style={{ marginBottom: 4 }}>
                    <strong>Заголовок</strong>
                  </div>
                  <Typography.Text>{currentTranslation.modalTitle || '—'}</Typography.Text>
                </div>
                <div>
                  <div style={{ marginBottom: 4 }}>
                    <strong>Описание</strong>
                  </div>
                  <Typography.Text>{currentTranslation.modalDescription || '—'}</Typography.Text>
                </div>
                <div style={{ marginTop: 8 }}>
                  <Typography.Text type={homeAdv?.showAtSessionStart ? 'success' : 'secondary'}>
                    {homeAdv?.showAtSessionStart ? '✓ Открыть в начале сессии' : 'Не открывать в начале сессии'}
                  </Typography.Text>
                </div>
              </>
            )}
          </div>
        </div>
      </Form>
    </Card>
  );
};

export default CompanyAdvHomeTab;
